import React, { useMemo, useState } from "react";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import FormActions from "../../../components/Form/FormActions";
import { COLORS } from "../../../constants";
import { Borda } from "../../../services/bordaService";
import { Tamanho } from "../../../services/tamanhoService";

interface SalaoModalProps {
  tamanhosSalao: Tamanho[];
  bordas: Borda[];
  isLoading: boolean;
  onCancel: () => void;
  onStartTimer: (
    tamanhoId: number,
    tamanhoNome: string,
    bordaId: number
  ) => void;
}

const SalaoModal: React.FC<SalaoModalProps> = ({
  tamanhosSalao,
  bordas,
  isLoading,
  onCancel,
  onStartTimer,
}) => {
  const [selectedTamanhoId, setSelectedTamanhoId] = useState<number | null>(
    null
  );
  const [selectedBordaId, setSelectedBordaId] = useState<number | null>(null);

  const tamanhoOptions = useMemo(
    () =>
      [...tamanhosSalao]
        .sort((a, b) =>
          a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
        )
        .map((tamanho) => ({ value: tamanho.id, label: tamanho.nome })),
    [tamanhosSalao]
  );

  const bordaOptions = useMemo(
    () =>
      [...bordas]
        .sort((a, b) =>
          a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
        )
        .map((borda) => ({ value: borda.id, label: borda.nome })),
    [bordas]
  );

  const selectedTamanhoOption =
    tamanhoOptions.find((option) => option.value === selectedTamanhoId) || null;
  const selectedBordaOption =
    bordaOptions.find((option) => option.value === selectedBordaId) || null;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedTamanhoId || !selectedTamanhoOption) {
      toast.error("Selecione um tamanho de salão.");
      return;
    }

    if (!selectedBordaId) {
      toast.error("Selecione uma borda.");
      return;
    }

    onStartTimer(
      selectedTamanhoId,
      selectedTamanhoOption.label,
      selectedBordaId
    );
  };

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      borderColor: COLORS.NEUTRAL_300,
      "&:hover": {
        borderColor: COLORS.PRIMARY,
      },
      "&:focus-within": {
        borderColor: COLORS.PRIMARY,
        boxShadow: "0 0 0 2px rgba(147, 32, 24, 0.2)",
      },
    }),
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700">
          Tamanho de salão
        </label>
        <ReactSelect
          options={tamanhoOptions}
          isLoading={isLoading}
          value={selectedTamanhoOption}
          onChange={(option) => setSelectedTamanhoId(option?.value || null)}
          placeholder={isLoading ? "Carregando tamanhos..." : "Selecione"}
          styles={selectStyles}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700">
          Borda
        </label>
        <ReactSelect
          options={bordaOptions}
          isLoading={isLoading}
          value={selectedBordaOption}
          onChange={(option) => setSelectedBordaId(option?.value || null)}
          placeholder={isLoading ? "Carregando bordas..." : "Selecione"}
          styles={selectStyles}
        />
      </div>

      <FormActions onCancel={onCancel} submitText="Iniciar Timer" />
    </form>
  );
};

export default SalaoModal;
